import React, { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import SimpleFormDialog, { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import { Typography, Button, Select, SelectChangeEvent, MenuItem, Box, Stack, Slider, Divider, Alert, useTheme, Paper, IconButton, Tooltip } from '@mui/material';
import APIRequest, { APIError } from "../../system/APIRequest";
import { socialDatasetId } from "system/query/Query";
import { useTranslation } from 'react-i18next';
import useFetchData from 'system/useFetchData';
import { currentUser } from "system/InfegyUser";
import { useNavigate } from "react-router-dom";
import { FetchWrapper } from "components/apiHandling/FetchWrapper";
import DatasetSelectionButton from "components/resources/datasets/DatasetSelectionButton";
import SavedQuerySelection from "components/queries/savedQueries/SavedQuerySelection";
import { permissionTest } from "system/InfegyUser";
import { DashboardDataDefaults, DashboardWidgetSpacing } from "components/resources/dashboards/DashboardDocument";
import StarIcon from '@mui/icons-material/Star';
import { enqueueSnackbar } from "notistack";

interface DashboardEditDialogOptions {
  dashboardId ?: string;
  setDialogIsOpen: (isOpen: boolean) => void;
  onClose: (reason?: "dashboardDeleted") => void;
}

// User editable properties of a dashboard
export interface DashboardDocumentProperties {
  name: string;
  description?: string | null;
  template_type: string | null;
  dataset_id: string | null;
  is_favorite?: boolean;

  data: {
    defaultWidgetQuery?: string;
    defaultDashboardQuery?: string;
    fullColumnCount?: number;
    widgetSpacing?: DashboardWidgetSpacing;
    autoRefreshInterval?: number;
    dashboardCacheInvalidatedAt?: string;
  };

  created_at?: string;
  updated_at?: string;
}

export default function DashboardEditDialog(props: DashboardEditDialogOptions): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();
  const { i18n } = useTranslation();

  const autoRefreshIntervalOptions = [
    { value: 0, label: i18n.t("dashboard.refresh-intervals.none") },
    // { value: 60, label: "1 minute" }, // For debugging, do not show in production
    { value: 900, label: i18n.t("dashboard.refresh-intervals.15-minutes") },
    { value: 1800, label: i18n.t("dashboard.refresh-intervals.30-minutes") },
    { value: 3600, label: i18n.t("dashboard.refresh-intervals.1-hour") },
    { value: 21600, label: i18n.t("dashboard.refresh-intervals.6-hours") }
  ];

  // Fetch the dashboard details if there is a dashboardId passed in
  const [dashboardResponse, dashboardIsLoading, dashboardError ] = useFetchData(props.dashboardId && `obj/dashboard/${props.dashboardId}`, [props.dashboardId]);
  const startingTitle:string = dashboardResponse?.output?.name || i18n.t('dashboard-title');

  const [dashboardTitle, setDashboardTitle] = useState<string>(dashboardResponse?.output?.name || "");
  const [isFavorite, setIsFavorite] = useState<boolean>(dashboardResponse?.output?.is_favorite || false);
  const [dashboardDescription, setDashboardDescription] = useState<string>(dashboardResponse?.output?.description || "");
  const [templateType, setTemplateType] = useState<string | null>(dashboardResponse?.output?.template_type || "");
  const [datasetId, setDatasetId] = useState<string | undefined>(dashboardResponse?.output?.dataset_id || undefined);

  const [defaultWidgetQueryId, setDefaultWidgetQueryId] = useState<string | undefined>(dashboardResponse?.output?.data?.defaultWidgetQuery);
  const [defaultWidgetQueryReset, setDefaultWidgetQueryReset] = useState<boolean>(false);
  const [defaultDashboardQueryId, setDefaultDashboardQueryId] = useState<string | undefined>(dashboardResponse?.output?.data?.defaultDashboardQuery);
  const [defaultDashboardQueryReset, setDefaultDashboardQueryReset] = useState<boolean>(false);

  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState<boolean>(false);

  const [fullColumnCount, setFullColumnCount] = useState<number>(DashboardDataDefaults.FullColumnCount);
  const [widgetSpacing, setWidgetSpacing] = useState<DashboardWidgetSpacing>(DashboardDataDefaults.WidgetSpacing);

  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | undefined>(dashboardResponse?.output?.data?.autoRefreshInterval || 0);

  const [submitWarning, setSubmitWarning] = useState<string | null>(null);

  const [hasQueryChanged, setHasQueryChanged] = useState<boolean>(false);

  // Treated as state so that if it is removed, the option still displays until the dialog is closed
  const [hasLegacyDefaultWidgetQuery, setHasLegacyDefaultWidgetQuery] = useState<boolean>(false);

  // Fetch the list of datasets as options for template dashboards
  const [datasetListReponse, datasetListIsLoading, datasetListError, ] = useFetchData("obj/dataset/?include_shared=true", []);
  const datasets:Array<any> = datasetListReponse?.output || [];

  const advancedOptionsForcedOpen = useMemo(() => {
    return (defaultDashboardQueryId || defaultWidgetQueryId) ? true : false;
  }, [defaultDashboardQueryId, defaultWidgetQueryId]);

  useEffect(() => {
    if (advancedOptionsForcedOpen) {
      setAdvancedOptionsOpen(true);
    }
  }, [advancedOptionsForcedOpen]);

  useEffect(() => {
    if (dashboardResponse?.output?.data?.autoRefreshInterval) {
      setAutoRefreshInterval(dashboardResponse?.output?.data?.autoRefreshInterval);
    }
  }, [dashboardResponse?.output?.data?.autoRefreshInterval])

  // If the dashboard is loaded, set the title, template type, and dataset defaults
  useEffect(() => {
    if (dashboardResponse?.output) {
      setTemplateType(dashboardResponse?.output?.template_type || "");
      setDashboardTitle(dashboardResponse?.output?.name || "");
      setIsFavorite(dashboardResponse?.output?.is_favorite || false);
      setDashboardDescription(dashboardResponse?.output?.description || "");
      setDatasetId(dashboardResponse?.output?.dataset_id || "");
      setDefaultWidgetQueryId(dashboardResponse?.output?.data?.defaultWidgetQuery);
      if (dashboardResponse?.output?.data?.defaultWidgetQuery) {
        setHasLegacyDefaultWidgetQuery(true);
      }

      setDefaultDashboardQueryId(dashboardResponse?.output?.data?.defaultDashboardQuery);

      setFullColumnCount(dashboardResponse?.output?.data?.fullColumnCount || DashboardDataDefaults.FullColumnCount);
      setWidgetSpacing(dashboardResponse?.output?.data?.widgetSpacing || DashboardDataDefaults.WidgetSpacing);
    }
  }, [dashboardResponse]);

  // If a template dashboard doesn't have a selected dataset, set a default
  useEffect(() => {
    const dashboard = dashboardResponse?.output;
    if (templateType) {
      if (!dashboard?.dataset_id && permissionTest("social_data")) {
        setDatasetId(socialDatasetId);
      } else if (!dashboard?.dataset_id && datasetListReponse?.output && datasetListReponse?.output?.length > 0) {
        setDatasetId(datasetListReponse?.output[0].id);
      }
    // We aren't in a template dashboard, so no dataset is needed
    } else if (!dashboard?.template_type) {
      setDatasetId(undefined);
    }
  }, [props.dashboardId, datasetListReponse, dashboardResponse, dashboardResponse?.output, dashboardResponse?.output?.template_type, templateType]);

  // Submit the dashboard - either create or update
  // depending on if there is a dashboardId passed in
  const submitDashboard = async () => {
    // If this is a template dashboard, we need to have a dataset selected
    if (templateType && !datasetId) {
      setSubmitWarning(i18n.t("dashboard.required-dataset-warning"));
      return;
    }
    // Require a dashboard title
    if (!dashboardTitle || dashboardTitle.trim() === "") {
      setSubmitWarning(i18n.t("dashboard.required-title-warning"));
      return;
    }

    // User editable
    const requestBody:DashboardDocumentProperties = {
      name: dashboardTitle,
      description: dashboardDescription,
      template_type: templateType,
      dataset_id: datasetId || null,
      data: {...(dashboardResponse?.output?.data || {})}
    };
    if (templateType !== "single" && templateType !== "multi") {
      requestBody["template_type"] = null;
      requestBody["dataset_id"] = null;
    }

    let URL = 'obj/dashboard/';
    let method = "POST";
    if (props.dashboardId) {
      URL += props.dashboardId;
      method = "PUT";
    }

    requestBody.data.defaultWidgetQuery = defaultWidgetQueryId;
    requestBody.data.defaultDashboardQuery = defaultDashboardQueryId;

    requestBody.data.fullColumnCount = fullColumnCount;
    requestBody.data.widgetSpacing = widgetSpacing;

    requestBody.data.autoRefreshInterval = autoRefreshInterval || 0;

    if (hasQueryChanged
      || defaultDashboardQueryId !== dashboardResponse?.output?.data?.defaultDashboardQuery
      || defaultWidgetQueryId !== dashboardResponse?.output?.data?.defaultWidgetQuery
      || templateType) {
      // we updated the default dashboard query, default widget query, or are in a template dashboard
      // so we need to save a new timestamp to invalidate the cache
      requestBody.data.dashboardCacheInvalidatedAt = new Date().toISOString();
      setHasQueryChanged(false);
    }

    try {
      const newDashboardResponse: any = await APIRequest(URL, {}, { method: method, "body": JSON.stringify(requestBody) });
      const newDashboardId = newDashboardResponse.id;
      if (!!dashboardResponse?.output?.is_favorite !== isFavorite) {
        // The user changed the favorite status of the dashboard.
        const favoriteRequestMethod = isFavorite ? "POST" : "DELETE";
        // The `newDashboardId` will only exist if a new dashboard was created.  If one was not
        // created, use the existing dashboard's id.
        const dashboardId = newDashboardId || props.dashboardId;
        await APIRequest(`favorite-resource/${dashboardId}`, {}, { method: favoriteRequestMethod }, true)
      }

      const queryResponse: any = newDashboardResponse;
      // Unset the Reset states and return early to keep the dialog open
      if (defaultDashboardQueryReset || defaultWidgetQueryReset) {
        setDefaultDashboardQueryReset(false);
        setDefaultWidgetQueryReset(false);
        return; 
      }

      // Close the dialog
      props.onClose();
      props.setDialogIsOpen(false);

      // Redirect to the dashboard page if it was created
      if (method === "POST") {
        navigate(`/dashboard/${queryResponse.id}`);
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, {variant: "error", persist: true});
    }
  }

  const deleteDashboard = () => {
    if (!props.dashboardId) {
      Sentry.captureMessage(`Unable to delete Dashboard: No dashboard ID`);
      return;
    }

    // Delete the dashboard
    APIRequest(`obj/dashboard/${props.dashboardId}`, {}, {
      method: "DELETE",
    }).then((response: any) => {
      props.onClose("dashboardDeleted");
      props.setDialogIsOpen(false);
    }).catch((error: APIError) => {
      console.error(`Error deleting page: ${error}`);
      Sentry.captureException(error);
    });
  }

  const activeUser = currentUser();

  const DashboardWidgetSpacingOptions = [
    { value: DashboardWidgetSpacing.MINIMAL, label: i18n.t("dashboard.spacing.minimal") },
    { value: DashboardWidgetSpacing.COMPACT, label: i18n.t("dashboard.spacing.compact") },
    { value: DashboardWidgetSpacing.COMFORTABLE, label: i18n.t("dashboard.spacing.comfortable") },
    { value: DashboardWidgetSpacing.SPACIOUS, label: i18n.t("dashboard.spacing.spacious") }
  ];

  return (
    <SimpleFormDialog
      isOpen={true}
      sx={{ minWidth:"700px" }}
      fullWidth={true}
      maxWidth="md"
      title={
        <>
        {(props.dashboardId && i18n.t('edit')) || i18n.t('add-new')}
        {' '}{i18n.t('nav.dashboard')}
        {dashboardResponse?.output?.name &&
          <>{' : '}{dashboardResponse?.output?.name }</>
        }
        </>
      }
      confirmText={
        <>
        {props.dashboardId && <>{i18n.t('dashboard.save-dashboard')}</>}
        {!props.dashboardId && <>{i18n.t('create')} {i18n.t('nav.dashboard')}</>}
        </>
      }
      onDelete={props.dashboardId ? () => { deleteDashboard(); } : undefined}
      onCancel={() => {
        props.onClose();
        props.setDialogIsOpen(false);
      }}
      onConfirm={() => {
        submitDashboard();
      }}>
      <Paper sx={{ m: 0, p: 2 }}>
        <Stack direction="row" sx={{
          gap: 1
        }}>
          <Box sx={{
            flex: 1
          }}>
            <SimpleFormLabel>{i18n.t("dashboard-title")}</SimpleFormLabel>
            <SimpleFormTextField
              value={dashboardTitle}
              onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDashboardTitle(event.target.value)}
              placeholder={startingTitle}
            />
          </Box>
          <Stack direction="column">
            <SimpleFormLabel>{i18n.t("favorite")}</SimpleFormLabel>
            <Box
              sx={{
                display: "flex",
                flex: 1,
                alignContent: "center",
                justifyContent: "center",
                marginBottom: "15px"
              }}>
              <Tooltip title={isFavorite ? i18n.t("dashboard.button-favorited") : i18n.t("dashboard.button-unfavorited")}>
                <IconButton
                  onClick={(event) => setIsFavorite(!isFavorite)}
                  sx={{color: isFavorite ? "gold" : theme.palette.grey[300]}}
                >
                  <StarIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </Stack>
        <SimpleFormLabel>{i18n.t("description")}</SimpleFormLabel>
        <SimpleFormTextField
          value={dashboardDescription}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDashboardDescription(event.target.value)}
        />
        <Box sx={{display:"grid", gridTemplateColumns:"1fr 1fr", gridGap:"10px"}}>
          <Box>
            <SimpleFormLabel>{i18n.t("dashboard.template-type")}</SimpleFormLabel>
            <Select
              variant="outlined"
              displayEmpty
              value={templateType || ""}
              renderValue={(value) =>
                <>
                  {value === "single" && <>{i18n.t("dashboard.template")}</>}
                  {value === "multi" && <>{i18n.t("multi")}</>}
                  {value !== "single" && value !== "multi" &&
                    <>{i18n.t("dashboard.freeform")}</>}
                </>
              }
              onChange={(event: SelectChangeEvent<string>) =>  {
                const type = event.target.value || null;
                setTemplateType(type);
              }} >
              <MenuItem value={""} sx={{ maxWidth:"400px", display:"block", whiteSpace:"normal" }}>
                <Typography>
                  <Typography sx={{ fontWeight:"bold" }}>{i18n.t("dashboard.freeform")}</Typography>
                  {i18n.t("dashboard.freeform-description")}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem value={"single"} sx={{ maxWidth:"400px", display:"block", whiteSpace:"normal" }}>
                <Typography>
                  <Typography sx={{ fontWeight:"bold" }}>{i18n.t("dashboard.template")}</Typography>
                  {i18n.t("dashboard.template-description")}
                </Typography>
              </MenuItem>
            </Select>
          </Box>
          <Box>
          {templateType &&
            <>
            <SimpleFormLabel>{i18n.t("dataset")}</SimpleFormLabel>
            {datasets.length > 1 &&
                <DatasetSelectionButton
                  selectedDatasetId={datasetId}
                  selectDefaultDataset={!props.dashboardId}
                  setSelectedDatasetId={(datasetId:string | undefined) => {
                    if (datasetId) {
                      setDatasetId(datasetId);
                      setDefaultWidgetQueryId(undefined);
                      setDefaultDashboardQueryId(undefined);
                      setHasQueryChanged(true);
                    }
                  }}
                />
            }
            {datasets.length === 1 &&
              <Typography sx={{display:"inline-block", fontWeight:'bold', fontSize:"1.1em"}}>
                {datasets[0].name}
              </Typography>
            }
            </>
          }
          {!templateType &&
          <>
            <SimpleFormLabel>{i18n.t("dashboard.auto-refresh-interval")}</SimpleFormLabel>
            <Select
              variant="outlined"
              displayEmpty
              value={autoRefreshInterval?.toString() || ""}
              onChange={(event: SelectChangeEvent<string>) =>  {
                let value = 0;
                if (event.target.value) {
                  value = parseInt(event.target.value)
                }
                setAutoRefreshInterval(value);
              }} >
              {autoRefreshIntervalOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </>
          }
          </Box>
        </Box>
        {!advancedOptionsForcedOpen &&
          <Button
            variant="text"
            sx={{mt:2}}
            onClick={() => {
              setAdvancedOptionsOpen(!advancedOptionsOpen);
            }} >
            {advancedOptionsOpen ? i18n.t("dashboard.hide-advanced-options") : i18n.t("dashboard.show-advanced-options")}
          </Button>
        }
        {(advancedOptionsForcedOpen || advancedOptionsOpen) &&
          <Box>
            <Box sx={{display:"grid", gridTemplateColumns:"1fr 1fr", gridGap:"10px"}}>
              <Box>
                <SimpleFormLabel>{i18n.t("dashboard.column-count")}</SimpleFormLabel>
                <Stack
                  direction="row"
                  sx={{
                    gap: 2,
                    alignItems: "center",
                    maxWidth: 300
                  }}>
                  <Slider
                    marks
                    max={20}
                    min={1}
                    onChange={(e: Event, value: number | number[]) => setFullColumnCount(value as number)}
                    step={1}
                    value={fullColumnCount} />
                  <Divider flexItem orientation="vertical" sx={{ width: "2px" }} />
                  <Box sx={{
                    width: 24
                  }}>{fullColumnCount}</Box>
                </Stack>
              </Box>
              <Box>
                <SimpleFormLabel>{i18n.t("dashboard.widget-spacing")}</SimpleFormLabel>
                <Select
                  variant="outlined"
                  displayEmpty
                  value={widgetSpacing}
                  onChange={(event: SelectChangeEvent<string>) =>  {
                    setWidgetSpacing(event.target.value as DashboardWidgetSpacing);
                  }} >
                  {DashboardWidgetSpacingOptions.map((option) => (
                    <MenuItem value={option.value} key={option.value} >
                      <Typography sx={{maxWidth:"450px", display:"block", whiteSpace:"normal", wordWrap:"break-word"}}>
                        {option.label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box sx={{mt:3}}>
              {!templateType &&
                <SimpleFormLabel tooltip={i18n.t("dashboard.root-dashboard-query-description")}>{i18n.t("dashboard.root-dashboard-query")}</SimpleFormLabel>
              }
              {!!templateType &&
                <SimpleFormLabel tooltip={i18n.t("dashboard.default-template-query-description")}>{i18n.t("dashboard.default-template-query")}</SimpleFormLabel>
              }
              <SavedQuerySelection
                datasetId={datasetId}
                selectedQueryId={defaultDashboardQueryId || ""}
                setSelectedQueryId={(queryId:string | undefined) => {
                  setHasQueryChanged(true);
                  if (queryId !== defaultDashboardQueryId) {
                    setDefaultDashboardQueryId(queryId);
                  }
                }}
                onMissing={() => {
                  setDefaultDashboardQueryId(undefined);
                  setDefaultDashboardQueryReset(true);
                }}
              />
              {defaultDashboardQueryReset &&
                <Alert severity="warning" sx={{ mt:1 }} action={
                  <Button onClick={submitDashboard} size="small">
                    {i18n.t("dashboard.default-template-query-reset-button")}
                  </Button>
                }>
                  {i18n.t("dashboard.default-template-query-removed-warning")}
                </Alert>
              }
            </Box>
            {hasLegacyDefaultWidgetQuery &&
            <Box sx={{mt:3}}>
              <SimpleFormLabel tooltip={i18n.t("dashboard.default-widget-query-description")}>{i18n.t("dashboard.default-widget-query")}</SimpleFormLabel>
              <SavedQuerySelection
                datasetId={datasetId}
                selectedQueryId={defaultWidgetQueryId || ""}
                setSelectedQueryId={(queryId:string | undefined) => {
                  setHasQueryChanged(true);
                  if (queryId !== defaultWidgetQueryId) {
                    setDefaultWidgetQueryId(queryId);
                  }
                }}
                onMissing={() => {
                  setDefaultWidgetQueryId(undefined);
                  setDefaultWidgetQueryReset(true);
                }}
              />
              {defaultWidgetQueryReset &&
                <Alert severity="warning" sx={{ mt:1 }} action={
                  <Button onClick={submitDashboard} size="small">
                    {i18n.t("dashboard.default-widget-query-reset-button")}
                  </Button>
                }>
                  {i18n.t("dashboard.default-widget-query-removed-warning")}
                </Alert>
              }
            </Box>
            }
            <br/>
            {props.dashboardId && props.dashboardId !== activeUser?.preferences?.default_dashboard &&
              <Button
                  sx={{mt:2}}
                  variant="contained"
                  type="submit"
                  onClick={() => {
                    const preferences = activeUser?.preferences || {};
                    preferences.default_dashboard = props.dashboardId;

                    if (templateType && !datasetId) {
                      // If this is a template dashboard, we need to have a dataset selected
                      setSubmitWarning(i18n.t("dashboard.required-dataset-warning"));
                      return;
                    }
                    if (!dashboardTitle || dashboardTitle.trim() === "") {
                      // Require a dashboard title
                      setSubmitWarning(i18n.t("dashboard.required-title-warning"));
                      return;
                    }

                    APIRequest(`obj/user/${activeUser?.id}`, {}, {
                      method: "PUT",
                      "body": JSON.stringify({
                        "preferences": preferences
                      })
                    }).then(() => {
                      // Close the dialog
                      if (activeUser) {
                        if (!activeUser?.preferences) {
                          activeUser.preferences = {};
                        }
                        activeUser.preferences.default_dashboard = props.dashboardId;
                      }
                      props.onClose();
                      props.setDialogIsOpen(false);
                      navigate(`/dashboard`);
                    }).catch((error: APIError) => {
                      console.error(`Error updating default homepage: ${error}`);
                      Sentry.captureException(error);
                    });
                  }}
                >
                  {i18n.t('dashboard.set-default-homepage')}
              </Button>
            }
          </Box>
        }
        {submitWarning &&
          <Alert severity="warning" sx={{mt:2}}>
            {submitWarning}
          </Alert>
        }
      <FetchWrapper isLoading={datasetListIsLoading || dashboardIsLoading} fetchError={datasetListError || dashboardError} />
      </Paper>
    </SimpleFormDialog>
  );
}
